/**
 * Service Registry
 * 
 * Central service locator for the trigger system.
 * Provides entity discovery, spawning with disposable pattern,
 * and tracks active disposables for per-frame updates.
 */

import { 
  Vector3, 
  PointLight, 
  SpotLight,
  ShadowGenerator,
  type Scene,
  type AbstractMesh
} from '@babylonjs/core';
import { logger } from './Logger';
import { EntityRegistry } from './EntityRegistry';
import { EntityType, type EntityInfo } from '../types/EntityTypes';
import type { 
  SceneServices, 
  Disposable, 
  DisposableEntry,
  PointLightOptions,
  SpotLightOptions
} from '../types/TriggerTypes';
import type { PhysicsManager } from '../systems/physics';
import type { ParticleManager } from '../systems/ParticleManager';
import type { CameraController } from '../systems/CameraController';
import type { SpatialCullingManager } from '../systems/SpatialCullingManager';
import type { FXManager } from '../systems/effects/FXManager';

// Debug layer mask - meshes with this layer mask should never cast shadows
const DEBUG_LAYER_MASK = 0x20000000;

/**
 * Configure a ShadowGenerator to exclude debug meshes globally.
 * Uses customAllowRendering callback to check layer mask at render time,
 * so debug meshes created AFTER the shadow generator are still excluded.
 */
function configureDebugMeshFilter(shadowGenerator: ShadowGenerator): void {
    // customAllowRendering is called for every submesh on every shadow render
    // Return false to exclude a mesh from shadow casting
    const existingFilter = shadowGenerator.customAllowRendering;
    shadowGenerator.customAllowRendering = (subMesh) => {
        const mesh = subMesh.getMesh();
        // Check layer mask - debug meshes use DEBUG_LAYER_MASK
        if ((mesh.layerMask & DEBUG_LAYER_MASK) !== 0) {
            return false; // Exclude from shadows
        }
        // Also check metadata for backwards compatibility
        if (mesh.metadata?.isDebug === true || mesh.metadata?.noShadows === true) {
            return false;
        }
        // Call existing filter if any
        if (existingFilter) {
            return existingFilter(subMesh);
        }
        return true; // Include by default
    };
}
import { DebugRenderer } from './DebugRenderer';

// ============================================
// DISPOSABLE FACTORY
// ============================================

/**
 * Create a disposable wrapper for an entity
 */
function createDisposable<T>(
  id: string,
  entity: T,
  cleanup: () => void
): Disposable<T> {
  let disposed = false;
  
  return {
    get entity() { return entity; },
    get id() { return id; },
    get isDisposed() { return disposed; },
    dispose() {
      if (disposed) return;
      disposed = true;
      cleanup();
    }
  };
}

// ============================================
// SERVICE REGISTRY IMPLEMENTATION
// ============================================

class ServiceRegistryImpl {
  private scene: Scene | null = null;
  private physics: PhysicsManager | null = null;
  private particles: ParticleManager | null = null;
  private camera: CameraController | null = null;
  private entityRegistry: EntityRegistry | null = null;
  private debugRenderer: DebugRenderer | null = null;
  private spatialCulling: SpatialCullingManager | null = null;
  private fxManager: FXManager | null = null;
  
  /** Active disposables with update loops */
  private activeDisposables = new Map<string, DisposableEntry>();
  
  /** Shadow generators created by lights */
  private shadowGenerators = new Map<string, ShadowGenerator>();
  
  /** Counter for unique IDs */
  private idCounter = 0;
  
  /** Stored gravity overrides per entity */
  private gravityOverrides = new Map<string, Vector3>();
  private originalGravities = new Map<string, Vector3>();
  
  /** Player death handler - can be overridden for custom death logic */
  private playerDeathHandler: ((cause?: string) => void) | null = null;
  
  /** Maximum simultaneous lights before shader recompilation warning.
   * This matches the maxSimultaneousLights we set on materials.
   * Babylon.js default is 4, we boost to 16 for PBR and 6 for Standard.
   */
  private static readonly MAX_LIGHTS_WARNING_THRESHOLD = 16;
  
  /** Track if we've warned about light count (to avoid spam) */
  private lightCountWarningIssued = false;
  
  // ============================================
  // HELPER METHODS
  // ============================================
  
  /**
   * Find entity info for a mesh by checking the mesh and its ancestors.
   * Returns undefined if no entity is associated with the mesh.
   */
  private findEntityForMesh(mesh: AbstractMesh): EntityInfo | undefined {
    if (!this.entityRegistry) return undefined;
    
    // Check all registered entities to see if any has this mesh or is an ancestor
    const allEntities = this.entityRegistry.getAllEntityInfos();
    
    for (const entity of allEntities) {
      if (entity.mesh) {
        // Check if this is the exact mesh
        if (entity.mesh === mesh) {
          return entity;
        }
        // Check if mesh is a descendant of entity mesh
        let parent = mesh.parent;
        while (parent) {
          if (parent === entity.mesh) {
            return entity;
          }
          parent = parent.parent;
        }
      }
    }
    
    return undefined;
  }
  
  // ============================================
  // INITIALIZATION
  // ============================================
  
  /**
   * Initialize the service registry with required services
   */
  initialize(deps: {
    scene: Scene;
    physics: PhysicsManager;
    particles: ParticleManager;
    camera: CameraController;
    entityRegistry: EntityRegistry;
  }): void {
    this.scene = deps.scene;
    this.physics = deps.physics;
    this.particles = deps.particles;
    this.camera = deps.camera;
    this.entityRegistry = deps.entityRegistry;
    this.debugRenderer = DebugRenderer.getInstance(deps.scene);
    logger.core.log('✅ ServiceRegistry initialized');
  }
  
  /**
   * Check if initialized
   */
  isInitialized(): boolean {
    return this.scene !== null;
  }
  
  /**
   * Set the spatial culling manager (called by LevelBuilder after creation)
   */
  setSpatialCulling(manager: SpatialCullingManager | null): void {
    this.spatialCulling = manager;
  }
  
  /**
   * Get the spatial culling manager (may be null if not yet created)
   */
  getSpatialCulling(): SpatialCullingManager | null {
    return this.spatialCulling;
  }
  
  /**
   * Set the FX manager (called after FXManager creation)
   */
  setFXManager(manager: FXManager | null): void {
    this.fxManager = manager;
  }
  
  /**
   * Get the FX manager (may be null if not yet created)
   */
  getFXManager(): FXManager | null {
    return this.fxManager;
  }
  
  /**
   * Set a custom player death handler.
   * If not set, uses default behavior (respawn at level start).
   * @param handler - Custom death handler, or null to reset to default
   */
  setPlayerDeathHandler(handler: ((cause?: string) => void) | null): void {
    this.playerDeathHandler = handler;
    logger.core.log(`💀 Player death handler ${handler ? 'set' : 'reset to default'}`);
  }
  
  /**
   * Handle player death event.
   * Calls custom handler if set, otherwise uses default respawn behavior.
   */
  private handlePlayerDeath(cause?: string): void {
    if (this.playerDeathHandler) {
      this.playerDeathHandler(cause);
      return;
    }
    
    // Default behavior: respawn at level start
    this.respawnPlayerAtStart(cause);
  }
  
  /**
   * Default respawn behavior - teleport player to level spawn point
   */
  private respawnPlayerAtStart(cause?: string): void {
    const player = this.entityRegistry?.getPlayer();
    if (!player || !this.physics) {
      logger.core.warn(`Cannot respawn player: ${!player ? 'no player' : 'no physics'}`);
      return;
    }
    
    // Get spawn point from physics (stored when level was built)
    const spawnPoint = this.physics.getSpawnPoint?.();
    if (!spawnPoint) {
      logger.core.warn('Cannot respawn player: no spawn point defined');
      return;
    }
    
    // Teleport player to spawn point
    this.physics.teleportBody(player.physicsBodyId, spawnPoint);
    logger.core.log(`💀 Player respawned at spawn point (cause: ${cause ?? 'unknown'})`);
  }
  
  // ============================================
  // UPDATE LOOP
  // ============================================
  
  /**
   * Update all active disposables with onUpdate callbacks
   */
  update(deltaTime: number): void {
    const services = this.getServices();
    
    for (const [id, entry] of this.activeDisposables) {
      // Clean up disposed entries
      if (entry.disposable.isDisposed) {
        this.activeDisposables.delete(id);
        continue;
      }
      
      // Call update if defined
      if (entry.onUpdate) {
        const entityInfo = this.entityRegistry?.getEntityInfo(id);
        if (entityInfo) {
          try {
            entry.onUpdate(deltaTime, entityInfo, services);
          } catch (error) {
            logger.core.error(`Error in disposable update for ${id}:`, error);
          }
        }
      }
    }
  }
  
  // ============================================
  // GET SERVICES
  // ============================================
  
  /**
   * Get the SceneServices interface for behaviors
   */
  getServices(): SceneServices {
    if (!this.scene || !this.physics || !this.particles || !this.camera || !this.entityRegistry || !this.debugRenderer) {
      throw new Error('ServiceRegistry not initialized');
    }
    
    const self = this;
    const scene = this.scene;
    const physics = this.physics;
    const particles = this.particles;
    const camera = this.camera;
    const entityRegistry = this.entityRegistry;
    const debugRenderer = this.debugRenderer;
    
    return {
      // Entity Discovery
      entities: {
        getAll(): EntityInfo[] {
          return entityRegistry.getAllEntityInfos();
        },
        getByType(type: EntityType): EntityInfo[] {
          return entityRegistry.getByType(type);
        },
        getById(id: string): EntityInfo | undefined {
          return entityRegistry.getEntityInfo(id);
        },
        getPlayer(): EntityInfo | undefined {
          return entityRegistry.getPlayer();
        },
        getByBehaviorId(behaviorId: string): EntityInfo[] {
          return entityRegistry.getAllEntityInfos().filter((e: EntityInfo) => e.behaviorId === behaviorId);
        }
      },
      
      // Entity Spawning
      spawn: {
        createPointLight(options: PointLightOptions): Disposable<PointLight> {
          return self.createPointLight(options);
        },
        createSpotLight(options: SpotLightOptions): Disposable<SpotLight> {
          return self.createSpotLight(options);
        },
        disposeById(entityId: string): boolean {
          return self.disposeById(entityId);
        }
      },
      
      // Direct access
      scene,
      physics,
      particles,
      camera,
      debug: debugRenderer,
      fx: self.fxManager ?? undefined,
      
      // Physics zones
      physicsZones: {
        setEntityGravity(entityId: string, gravity: Vector3): void {
          self.setEntityGravity(entityId, gravity);
        },
        resetEntityGravity(entityId: string): void {
          self.resetEntityGravity(entityId);
        },
        isEntityInZone(_entityId: string, _zoneId?: string): boolean {
          // Zone containment is tracked by TriggerSystem, not ServiceRegistry
          // Use TriggerSystem.isEntityInTrigger() for zone checks
          return false;
        }
      },
      
      // Player events
      player: {
        onDeath(cause?: string): void {
          self.handlePlayerDeath(cause);
        }
      }
    };
  }
  
  // ============================================
  // ENTITY SPAWNING
  // ============================================
  
  /**
   * Create a point light with disposable pattern.
   * Lights are created directly; culling system manages which are enabled.
   */
  private createPointLight(options: PointLightOptions): Disposable<PointLight> {
    if (!this.scene || !this.entityRegistry) {
      throw new Error('ServiceRegistry not initialized');
    }
    
    const id = `spawned_light_${++this.idCounter}`;
    
    // Create light directly
    // NOTE: With UnifiedLightManager's fixed pool, most scene lights are pool lights
    // that never change enabled state. Spawned lights (like cave_light trigger) are
    // outside the pool and can be enabled directly without causing major recompilation.
    const light = new PointLight(options.name || id, options.position, this.scene);
    light.setEnabled(true); // Enable directly - pool architecture handles most lights
    light.intensity = options.intensity ?? 1.0;
    light.range = options.range ?? 20;
    if (options.diffuse) light.diffuse = options.diffuse;
    if (options.specular) light.specular = options.specular;
    
    // Check light threshold
    this.checkLightCount();
    
    // Register in EntityRegistry
    this.entityRegistry.registerEntityInfo({
      id,
      type: EntityType.DYNAMIC_OBJECT,
      behaviorId: 'spawned_light',
      displayName: options.name || 'Spawned Light',
      isPlayer: false,
      metadata: { lightType: 'point' }
    });
    
    logger.triggers.debug(`Created point light: ${id}`);
    
    const disposable = createDisposable(id, light, () => {
      this.cleanupLight(id, light);
    });
    
    // Track for update loop if onUpdate provided
    if (options.onUpdate) {
      this.activeDisposables.set(id, {
        disposable,
        onUpdate: options.onUpdate
      });
    }
    
    return disposable;
  }
  
  /**
   * Create a spot light with optional shadows
   */
  private createSpotLight(options: SpotLightOptions): Disposable<SpotLight> {
    if (!this.scene || !this.entityRegistry) {
      throw new Error('ServiceRegistry not initialized');
    }
    
    const id = `spawned_spotlight_${++this.idCounter}`;
    
    // Create SpotLight exactly like the Babylon.js example:
    // new BABYLON.SpotLight("spotLight", position, direction, angle, exponent, scene)
    const light = new SpotLight(
      options.name || id,
      options.position,
      options.direction,
      options.angle ?? Math.PI / 4,
      options.exponent ?? 2,
      this.scene
    );
    
    // Set basic properties
    light.intensity = options.intensity ?? 1.0;
    light.range = options.range ?? 30;
    if (options.diffuse) light.diffuse = options.diffuse;
    if (options.specular) light.specular = options.specular;
    
    // IMPORTANT: innerAngle only works with FALLOFF_GLTF mode
    if (options.innerAngle !== undefined) {
      light.falloffType = SpotLight.FALLOFF_GLTF;
      light.innerAngle = options.innerAngle;
    }
    
    console.log(`💡 createSpotLight: ${options.name || id}`);
    console.log(`   pos=(${light.position.x.toFixed(2)}, ${light.position.y.toFixed(2)}, ${light.position.z.toFixed(2)})`);
    console.log(`   dir=(${light.direction.x.toFixed(2)}, ${light.direction.y.toFixed(2)}, ${light.direction.z.toFixed(2)})`);
    console.log(`   intensity=${light.intensity} range=${light.range} angle=${light.angle.toFixed(2)} exponent=${light.exponent}`);
    console.log(`   isEnabled=${light.isEnabled()} name=${light.name}`);
    
    // CRITICAL: Move this light to the FRONT of scene.lights array
    // Babylon.js uses the first N lights from scene.lights for materials.
    // Spawned lights should take priority over ambient/pool lights.
    const lightIndex = this.scene.lights.indexOf(light);
    if (lightIndex > 0) {
      this.scene.lights.splice(lightIndex, 1); // Remove from current position
      this.scene.lights.unshift(light); // Add to front
      console.log(`   Moved to front of scene.lights (was index ${lightIndex}, now 0)`);
    }
    
    // Create shadow generator if enabled
    if (options.shadowEnabled) {
      const shadowGenerator = new ShadowGenerator(
        options.shadowMapSize ?? 1024,
        light
      );
      
      // Use PCF (Percentage Closer Filtering) for clean shadows
      // PCF produces sharper shadows than BlurExponentialShadowMap which can create soft blobs
      shadowGenerator.usePercentageCloserFiltering = true;
      shadowGenerator.filteringQuality = ShadowGenerator.QUALITY_HIGH;
      
      // Enable transparent shadows (respects material alpha)
      shadowGenerator.transparencyShadow = true;
      shadowGenerator.enableSoftTransparentShadow = true;
      
      // CRITICAL: Set shadow bias to prevent self-shadowing artifacts
      // Without bias, meshes shadow themselves and appear pitch black
      shadowGenerator.bias = 0.005; // Offset shadow map to reduce self-shadowing
      shadowGenerator.normalBias = 0.02; // Additional offset along surface normal
      
      // Smooth falloff at shadow map frustum edges to avoid hard cutoff
      shadowGenerator.frustumEdgeFalloff = 1.0;
      
      // CRITICAL: Configure filter to exclude debug meshes by layer mask
      // This works for meshes created AFTER the shadow generator too
      configureDebugMeshFilter(shadowGenerator);
      
      if (options.shadowMinZ !== undefined) light.shadowMinZ = options.shadowMinZ;
      if (options.shadowMaxZ !== undefined) light.shadowMaxZ = options.shadowMaxZ;
      
      this.shadowGenerators.set(id, shadowGenerator);
      
      // Configure shadow casters and receivers:
      // - Level geometry (STATIC_GEOMETRY): CAST + RECEIVE shadows
      // - Visual entities (CHARACTER, DYNAMIC_OBJECT, PROJECTILE, DEBRIS): CAST + RECEIVE shadows  
      // - Debug meshes: NO cast, NO receive
      let casterCount = 0;
      let receiverCount = 0;
      
      this.scene.meshes.forEach(mesh => {
        // Skip invisible meshes entirely
        if (!mesh.isVisible) return;
        
        // Check if this is a debug mesh - these should NOT cast or receive shadows
        // Debug meshes are identified by metadata flags set at creation time
        const isDebugMesh = mesh.metadata?.isDebug === true ||
                            mesh.metadata?.noShadows === true;
        
        if (isDebugMesh) {
          mesh.receiveShadows = false;
          return; // Don't add as caster either
        }
        
        // Check mesh metadata for explicit shadow control
        const noShadows = mesh.metadata?.noShadows === true;
        const forceCastShadow = mesh.metadata?.castShadow === true;
        const forceReceiveShadow = mesh.metadata?.receiveShadow === true;
        
        // Determine if this mesh should cast shadows
        let shouldCastShadow = false;
        
        // Check if mesh is associated with a registered entity
        const entityInfo = this.findEntityForMesh(mesh);
        if (entityInfo) {
          // Visual entities and static geometry cast shadows
          const castingTypes = [
            EntityType.CHARACTER,
            EntityType.DYNAMIC_OBJECT,
            EntityType.PROJECTILE,
            EntityType.DEBRIS,
            EntityType.STATIC_GEOMETRY // Level geometry casts shadows too
          ];
          shouldCastShadow = castingTypes.includes(entityInfo.type);
        } else {
          // Fallback: meshes with skeletons (animated characters) should cast
          if (mesh.skeleton) {
            shouldCastShadow = true;
          }
          // Fallback: meshes with hm_mesh_role='render' should cast
          const meshRole = mesh.metadata?.gltf?.extras?.hm_mesh_role || mesh.metadata?.hm_mesh_role;
          if (meshRole === 'render') {
            shouldCastShadow = true;
          }
          // Fallback: level geometry (meshes without entity registration) should cast
          // These are typically imported from glTF maps
          if (mesh.getTotalVertices() > 0 && mesh.getTotalVertices() < 50000) {
            shouldCastShadow = true;
          }
        }
        
        // Override with explicit metadata
        if (forceCastShadow) shouldCastShadow = true;
        if (noShadows) shouldCastShadow = false;
        
        // Add shadow caster
        if (shouldCastShadow && mesh.getTotalVertices() > 0 && mesh.getTotalVertices() < 50000) {
          shadowGenerator.addShadowCaster(mesh, true); // true = include descendants
          casterCount++;
        }
        
        // All visible non-debug geometry receives shadows by default
        if (!noShadows && (forceReceiveShadow || mesh.getTotalVertices() > 0)) {
          mesh.receiveShadows = true;
          receiverCount++;
        }
      });
      
      // CRITICAL: Filter out debug meshes from shadow render list
      // Debug meshes must have isDebug or noShadows metadata set at creation time
      const shadowMap = shadowGenerator.getShadowMap();
      if (shadowMap?.renderList) {
        const originalLength = shadowMap.renderList.length;
        shadowMap.renderList = shadowMap.renderList.filter(mesh => {
          const isDebug = mesh.metadata?.isDebug === true ||
                          mesh.metadata?.noShadows === true;
          return !isDebug;
        });
        const filteredCount = originalLength - shadowMap.renderList.length;
        if (filteredCount > 0) {
          logger.triggers.debug(`Filtered ${filteredCount} debug meshes from shadow render list`);
        }
      }
      
      logger.triggers.debug(`Created shadow generator for: ${id} (${casterCount} casters, ${receiverCount} receivers)`);
    }
    
    // Register in EntityRegistry
    this.entityRegistry.registerEntityInfo({
      id,
      type: EntityType.DYNAMIC_OBJECT,
      behaviorId: 'spawned_spotlight',
      displayName: options.name || 'Spawned Spotlight',
      isPlayer: false,
      metadata: { 
        lightType: 'spot',
        hasShadows: options.shadowEnabled ?? false
      }
    });
    
    logger.triggers.log(`🔦 Created spotlight: ${id} (shadows: ${options.shadowEnabled ?? false})`);
    
    // Check if we're exceeding light threshold
    this.checkLightCount();
    
    const disposable = createDisposable(id, light, () => {
      this.cleanupLight(id, light);
    });
    
    // Track for update loop if onUpdate provided
    if (options.onUpdate) {
      this.activeDisposables.set(id, {
        disposable,
        onUpdate: options.onUpdate
      });
    }
    
    return disposable;
  }
  
  /**
   * Clean up a light entity (non-pooled)
   */
  private cleanupLight(id: string, light: PointLight | SpotLight): void {
    // Dispose shadow generator if exists
    const shadowGen = this.shadowGenerators.get(id);
    if (shadowGen) {
      shadowGen.dispose();
      this.shadowGenerators.delete(id);
    }
    
    // Dispose light
    light.dispose();
    
    // Unregister from EntityRegistry
    this.entityRegistry?.unregisterEntityInfo(id);
    
    // Remove from active disposables
    this.activeDisposables.delete(id);
    
    logger.triggers.debug(`Disposed light: ${id}`);
    
    // Reset warning flag when lights are disposed so we can warn again if needed
    this.lightCountWarningIssued = false;
  }
  
  /**
   * Check if we're exceeding the light threshold and warn if so.
   * This helps detect potential shader recompilation issues.
   */
  private checkLightCount(): void {
    if (!this.scene) return;
    
    // Count enabled lights only
    const enabledLights = this.scene.lights.filter(l => l.isEnabled()).length;
    
    if (enabledLights > ServiceRegistryImpl.MAX_LIGHTS_WARNING_THRESHOLD && !this.lightCountWarningIssued) {
      logger.core.warn(`⚠️ Light count (${enabledLights}) exceeds threshold (${ServiceRegistryImpl.MAX_LIGHTS_WARNING_THRESHOLD}). ` +
        `This may cause shader recompilations and visual glitches. Consider reusing lights or increasing maxSimultaneousLights on materials.`);
      this.lightCountWarningIssued = true;
    }
  }
  
  /**
   * Get current light statistics for debugging
   */
  getLightStats(): { total: number; enabled: number; threshold: number; overThreshold: boolean } {
    const total = this.scene?.lights.length ?? 0;
    const enabled = this.scene?.lights.filter(l => l.isEnabled()).length ?? 0;
    return {
      total,
      enabled,
      threshold: ServiceRegistryImpl.MAX_LIGHTS_WARNING_THRESHOLD,
      overThreshold: enabled > ServiceRegistryImpl.MAX_LIGHTS_WARNING_THRESHOLD
    };
  }
  
  /**
   * Dispose an entity by ID
   */
  private disposeById(entityId: string): boolean {
    const entry = this.activeDisposables.get(entityId);
    if (entry && !entry.disposable.isDisposed) {
      entry.disposable.dispose();
      return true;
    }
    return false;
  }
  
  // ============================================
  // GRAVITY OVERRIDES
  // ============================================
  
  /**
   * Set gravity override for an entity
   */
  private setEntityGravity(entityId: string, gravity: Vector3): void {
    // Store the override
    if (!this.originalGravities.has(entityId)) {
      // Store current gravity as original (use scene default)
      this.originalGravities.set(entityId, this.scene?.gravity?.clone() ?? new Vector3(0, -9.81, 0));
    }
    this.gravityOverrides.set(entityId, gravity.clone());
    logger.triggers.debug(`Set gravity for ${entityId}: ${gravity}`);
  }
  
  /**
   * Reset entity to default gravity
   */
  private resetEntityGravity(entityId: string): void {
    this.gravityOverrides.delete(entityId);
    this.originalGravities.delete(entityId);
    logger.triggers.debug(`Reset gravity for ${entityId}`);
  }
  
  /**
   * Get gravity override for an entity (used by physics system)
   */
  getEntityGravityOverride(entityId: string): Vector3 | undefined {
    return this.gravityOverrides.get(entityId);
  }
  
  // ============================================
  // CLEANUP
  // ============================================
  
  /**
   * Dispose all spawned entities
   */
  disposeAll(): void {
    for (const [_id, entry] of this.activeDisposables) {
      if (!entry.disposable.isDisposed) {
        entry.disposable.dispose();
      }
    }
    this.activeDisposables.clear();
    this.gravityOverrides.clear();
    this.originalGravities.clear();
    logger.core.debug('ServiceRegistry: Disposed all entities');
  }
  
  /**
   * Full cleanup
   */
  dispose(): void {
    this.disposeAll();
    this.scene = null;
    this.physics = null;
    this.particles = null;
    this.camera = null;
    this.entityRegistry = null;
  }
}

// Singleton instance
export const ServiceRegistry = new ServiceRegistryImpl();
